import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Truck, ShieldCheck, Minus, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { shopApi, gbp2 } from "@/lib/shopApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { ProductCard } from "@/pages/shop/Shop";
import { ProductReviews, SaveButton } from "@/components/shop/ShopExtras";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(undefined);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);

  const p = data?.product;
  usePageMeta(p?.title || "Product", p?.description?.slice(0, 150) || "A product on the Fable + shop.");

  useEffect(() => {
    let alive = true;
    setData(undefined);
    shopApi.product(id).then((d) => alive && setData(d)).catch((e) => alive && setData({ error: e?.status === 404 ? "This product isn't available." : e?.message || "Couldn't load this product." }));
    return () => { alive = false; };
  }, [id, user?.id]);

  // Back from Stripe: ?session_id=… → confirm the order
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return undefined;
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const r = await shopApi.orderStatus(sid);
        if (!alive) return;
        if (r.paid) { setPaid(r.order || {}); return; }
      } catch { /* retry */ }
      if (++tries < 12 && alive) setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [params]);
  useEffect(() => {
    if (params.get("cancel") === "1") {
      toast("No charge was made.");
      const n = new URLSearchParams(params); n.delete("cancel"); setParams(n, { replace: true });
    }
  }, [params, setParams]);

  const buy = async () => {
    if (!user) { navigate("/login", { state: { from: `/shop/${id}` } }); return; }
    setBusy(true);
    try {
      const r = await shopApi.checkout(id, { qty, note: note.trim() || undefined, return_base: window.location.origin });
      if (r.url) { window.location.assign(r.url); return; }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout.");
    } finally {
      setBusy(false);
    }
  };

  if (data === undefined) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6"><div className="grid gap-8 lg:grid-cols-2"><Skeleton className="aspect-square rounded-3xl" /><div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-6 w-24" /><Skeleton className="h-24" /></div></div></div>
    );
  }
  if (data.error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <p className="font-semibold">{data.error}</p>
        <Link to="/shop"><Button variant="outline" className="mt-5 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> Back to the shop</Button></Link>
      </div>
    );
  }

  const images = (p.images && p.images.length ? p.images : [p.cover]).filter(Boolean);
  const off = p.compare_at_gbp && p.compare_at_gbp > p.price_gbp;
  const maxQty = p.stock != null ? Math.max(0, Math.min(20, p.stock)) : 20;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-testid={`product-detail-${p.id}`}>
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Shop</Link>
      {paid && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900" data-testid="order-paid">
          <CheckCircle2 size={22} />
          <div className="flex-1 text-sm"><p className="font-semibold">Order placed{paid.product_title ? ` — ${paid.product_title}` : ""}.</p><p>The seller has been told and will post it. Track it in <Link to="/shop/orders" className="underline">My orders</Link>.</p></div>
        </div>
      )}
      <div className="mt-4 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="aspect-square overflow-hidden rounded-3xl bg-muted">
            {images[img] && <img src={images[img]} alt={p.title} className="h-full w-full object-cover" />}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2">
              {images.map((src, i) => (
                <button key={src + i} type="button" onClick={() => setImg(i)} aria-label={`Image ${i + 1}`} className={`h-16 w-16 overflow-hidden rounded-xl border ${i === img ? "border-foreground" : "border-border"}`}><img src={src} alt="" className="h-full w-full object-cover" /></button>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold capitalize text-primary">{p.category}</p>
          <div className="flex items-start gap-3"><h1 className="mt-1 flex-1 font-serif-display text-3xl font-bold leading-tight">{p.title}</h1>{!p.mine && <SaveButton id={p.id} className="mt-1 shrink-0" />}</div>
          {p.rating?.count ? <p className="mt-1 text-sm text-muted-foreground">★ <b className="text-foreground">{p.rating.avg}</b> · {p.rating.count} review{p.rating.count === 1 ? "" : "s"}</p> : null}
          <p className="mt-3 font-mono-num text-2xl font-bold">{gbp2(p.price_gbp)} {off && <span className="ml-2 text-base font-normal text-muted-foreground line-through">{gbp2(p.compare_at_gbp)}</span>}</p>
          {p.seller && (
            <Link to={p.seller.handle ? `/@${p.seller.handle}` : `/shop/store/${p.seller_id}`} className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-card p-3 hover:bg-muted/60" data-testid="seller-link">
              <CreatorAvatar author={p.seller} size={40} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 font-semibold">{p.seller.business_name || p.seller.display_name} <VerifiedMark author={p.seller} /></p>
                <p className="text-xs text-muted-foreground">{p.seller.handle ? `@${p.seller.handle} · ` : ""}{p.seller.sold || 0} sold</p>
              </div>
              <span className="text-sm font-medium text-primary">View store</span>
            </Link>
          )}
          {p.description && <p className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed">{p.description}</p>}
          <div className="mt-5 space-y-1.5 text-sm text-muted-foreground">
            {p.shipping_note && <p className="flex items-center gap-2"><Truck size={15} /> {p.shipping_note}</p>}
            <p className="flex items-center gap-2"><ShieldCheck size={15} /> Paid through Stripe; the seller never sees your card.</p>
            {p.stock != null && <p>{p.stock === 0 ? "Sold out" : `${p.stock} in stock`}</p>}
          </div>
          {!p.mine && (
            <div className="mt-6 rounded-2xl border border-border bg-card p-4" data-testid="buy-box">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Quantity</span>
                <div className="inline-flex items-center rounded-full border border-border">
                  <button type="button" aria-label="Fewer" disabled={qty <= 1} onClick={() => setQty(qty - 1)} className="p-2 disabled:opacity-40"><Minus size={14} /></button>
                  <span className="w-6 text-center font-mono-num text-sm font-semibold">{qty}</span>
                  <button type="button" aria-label="More" disabled={qty >= maxQty} onClick={() => setQty(qty + 1)} className="p-2 disabled:opacity-40"><Plus size={14} /></button>
                </div>
              </div>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Size, colour or delivery note (optional)" className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" data-testid="order-note" />
              <Button className="mt-3 w-full rounded-full" disabled={busy || p.sold_out || maxQty === 0} onClick={buy} data-testid="buy-btn">
                {p.sold_out ? "Sold out" : busy ? "One moment…" : user ? `Buy · ${gbp2(p.price_gbp * qty)}` : "Sign in to buy"}
              </Button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">Test payments · no real card charges</p>
            </div>
          )}
          {p.mine && <p className="mt-6 text-sm text-muted-foreground">This is your product. Manage it from the Fable + app.</p>}
        </div>
      </div>
      <ProductReviews id={p.id} mine={p.mine} />
      {data.more_from_seller?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif-display text-2xl font-bold">More from {p.seller?.business_name || p.seller?.display_name || "this seller"}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">{data.more_from_seller.map((m) => <ProductCard key={m.id} p={m} />)}</div>
        </section>
      )}
    </div>
  );
}
